import type React from 'react';
import { useAlohaScroll } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/useAlohaScroll';
import type { AlohaScrollControllerProps } from './AlohaScrollController.types';

/**
 * Hosts `useAlohaScroll` so its state does not live in the layout.
 *
 * The hook returns nothing — the layout called it purely for its side effects — but its four
 * internal `useState`s (the resolved current page, the scrolled and normalized flags, the page-ref
 * list) sat in the layout's own hook list. Every scroll step therefore re-rendered the layout and
 * all ~83 components under it for state the layout never reads. Rendering `null` from here keeps
 * the behaviour identical and confines those updates to this component.
 *
 * Ordering is preserved by being a descendant, not by sibling position. Callback refs attach during
 * the commit phase and `useEffect` flushes after the whole tree has committed, so the section refs
 * are populated before this hook's effects run wherever it sits. React runs a parent's effects after
 * all of its children's, which is what keeps these effects ahead of the layout's own — the same
 * order they had when the hook lived there.
 */
export const AlohaScrollController: React.FC<AlohaScrollControllerProps> = ({ pages, pagesRef, container, setIsLastPageVisible, setIsPastFirstPage }) => {
  useAlohaScroll(pages, pagesRef, container, setIsLastPageVisible, setIsPastFirstPage);
  return null;
};

AlohaScrollController.displayName = 'AlohaScrollController';
